import type { Schema } from '@dealdesk/api-schema';
import { useTranslation } from 'react-i18next';

import { Icon, Tooltip, cn } from '@/design-system';
import { formatDateTime } from '@/shared/lib/format';

/** Bank-written stage codes ("5-UW", "7-PS") are shown; machine keys ("CREDIT_RETURN") never are — the label says it. */
const isBankCode = (code: string) => code.length <= 8 && !code.includes('_');

/**
 * The bank's own operational stage, secondary to the macro stage: muted code · label, never a second coloured badge.
 * Codes and labels come from the bank's catalogue as written (not translated).
 */
/** `withIcon`: a small bank glyph for record headers (key record fact); never in table cells. */
export function BankStageBadge({ stage, className, withSince, withIcon }: { stage: Schema<'BankStageRef'> | null; className?: string; withSince?: boolean; withIcon?: boolean }) {
  const { t } = useTranslation();
  if (!stage) return null;
  return (
    <Tooltip content={t('workflow.bankStage.since', { time: formatDateTime(stage.since) })}>
      <span className={cn('inline-flex min-w-0 items-baseline gap-1 text-table text-ink-secondary', className)}>
        {withIcon ? <Icon name="concept.bank" size="xs" tone="muted" className="self-center" /> : null}
        <span className="sr-only">{t('workflow.bankStage.label')}</span>
        {isBankCode(stage.code) ? (
          <>
            <bdi dir="ltr" className="font-mono text-meta text-ink">
              {stage.code}
            </bdi>
            <span aria-hidden="true" className="text-border-strong">
              ·
            </span>
          </>
        ) : null}
        <span className="truncate">{stage.label}</span>
        {stage.unmapped ? <span className="text-meta font-medium text-warning">{t('workflow.bankStage.unmapped')}</span> : null}
        {withSince ? <span className="text-meta text-ink-muted">{t('workflow.bankStage.sinceShort', { time: formatDateTime(stage.since) })}</span> : null}
      </span>
    </Tooltip>
  );
}

/** Every bank-stage movement, newest first. Backward moves are marked in words, not only by colour. */
export function BankStageHistory({ events, className }: { events: Schema<'BankStageEvent'>[]; className?: string }) {
  const { t } = useTranslation();
  if (!events.length) return <p className={cn('py-2 text-body text-ink-secondary', className)}>{t('workflow.bankStage.empty')}</p>;
  return (
    <ol className={cn('flex flex-col', className)} aria-label={t('workflow.bankStage.history')}>
      {events.map((e, i) => (
        <li key={e.id} className="flex items-start gap-3 border-b border-border py-2 last:border-b-0">
          <bdi dir="ltr" className="w-24 shrink-0 font-mono text-meta text-ink">
            {isBankCode(e.code) ? e.code : ''}
          </bdi>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-table text-ink">
              {e.label}
              {e.direction === 'backward' ? <span className="ms-2 text-meta font-medium text-warning">{t('workflow.bankStage.movedBack', { from: events[i + 1]?.label ?? (e.fromCode && isBankCode(e.fromCode) ? e.fromCode : '') })}</span> : null}
              {e.direction === 'off_path' ? <span className="ms-2 text-meta text-ink-muted">{t('workflow.bankStage.offPath')}</span> : null}
            </span>
            {e.note ? (
              <span className="text-meta text-ink-secondary" dir="auto">
                {e.note}
              </span>
            ) : null}
            <span className="text-meta text-ink-muted">
              {e.by ? e.by.name : t(`workflow.bankStage.source.${e.source}`)}
              {e.bucket ? '' : ` · ${t('workflow.bankStage.unmapped')}`}
            </span>
          </div>
          <time dateTime={e.at} className="tabular shrink-0 text-meta text-ink-muted">
            {formatDateTime(e.at)}
          </time>
        </li>
      ))}
    </ol>
  );
}
